import React, { useState, useRef, useEffect } from "react";
import { MdEmail } from "react-icons/md";
import { FaChevronDown, FaBars, FaTimes, FaChevronRight, FaLock, FaLockOpen, FaUser } from "react-icons/fa";
import { FaPhoneAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

const Header = () => {
  // State variables for managing UI
  const [cardVisible, setCardVisible] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openNestedDropdown, setOpenNestedDropdown] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarDropdown, setSidebarDropdown] = useState(null);
  const [sidebarNestedDropdown, setSidebarNestedDropdown] = useState(null);
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const [memberData, setMemberData] = useState(null);
  
  // Track how dropdown was opened (hover vs click)
  const [dropdownOpenedBy, setDropdownOpenedBy] = useState({});
  const [nestedDropdownOpenedBy, setNestedDropdownOpenedBy] = useState({});
  
  const cardRef = useRef();
  const dropdownRef = useRef();
  const sidebarRef = useRef();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();

  // Check member login status on mount and when storage changes
  useEffect(() => {
    const checkMemberLoginStatus = () => {
      const token = localStorage.getItem('member_token');
      const storedMemberData = localStorage.getItem('member_data');
      const tokenExpiry = localStorage.getItem('token_expiry');
      
      if (token && storedMemberData && tokenExpiry) {
        const now = Date.now();
        const expiryTime = parseInt(tokenExpiry, 10);
        
        if (now < expiryTime) {
          // Token is still valid
          setIsMemberLoggedIn(true);
          setMemberData(JSON.parse(storedMemberData));
          return;
        } else {
          // Token expired, clear storage
          localStorage.removeItem('member_token');
          localStorage.removeItem('member_data');
          localStorage.removeItem('token_expiry');
        }
      }
      
      // No valid token or token expired
      setIsMemberLoggedIn(false);
      setMemberData(null);
    };
    
    // Check initially
    checkMemberLoginStatus();
    
    // Listen for storage changes (for logout/login from other tabs/components)
    const handleStorageChange = (e) => {
      if (e.key === 'member_token' || e.key === 'member_data' || e.key === 'token_expiry') {
        checkMemberLoginStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically (every 5 seconds) in case of in-app changes
    const interval = setInterval(checkMemberLoginStatus, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Window resize handle
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1175.56) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Card
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setCardVisible(false);
      }
     
      if (openDropdown && dropdownRef.current &&
          !dropdownRef.current.contains(e.target)) {
        // Check if click is on any dropdown item
        const isDropdownItem = e.target.closest('.dla-dropdown-item') ||
                             e.target.closest('.dla-shop-main') ||
                             e.target.closest('.dla-trade-main') ||
                             e.target.closest('.dropdown-item') ||
                             e.target.closest('.dropdown-arrow');
       
        if (!isDropdownItem) {
          setOpenDropdown(null);
          setOpenNestedDropdown(null);
          setDropdownOpenedBy({});
          setNestedDropdownOpenedBy({});
        }
      }
      
      // Sidebar
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(e.target) &&
          !e.target.closest('[data-menu-button]')) {
        setSidebarOpen(false);
      }
    };
   
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown, sidebarOpen]);

  // Desktop dropdown toggle via CLICK
  const toggleDropdownByClick = (name) => {
    const isCurrentlyOpen = openDropdown === name;
   
    if (isCurrentlyOpen && dropdownOpenedBy[name] === 'click') {
      // If it was opened by click and we're clicking again, close it
      setOpenDropdown(null);
      setDropdownOpenedBy(prev => ({ ...prev, [name]: null }));
    } else {
      // Open by click
      setOpenDropdown(name);
      setOpenNestedDropdown(null);
      setDropdownOpenedBy(prev => ({ ...prev, [name]: 'click' }));
      setNestedDropdownOpenedBy({});
    }
  };

  // Desktop nested dropdown toggle via CLICK
  const toggleNestedDropdownByClick = (name) => {
    const isCurrentlyOpen = openNestedDropdown === name;
   
    if (isCurrentlyOpen && nestedDropdownOpenedBy[name] === 'click') {
      // If it was opened by click and we're clicking again, close it
      setOpenNestedDropdown(null);
      setNestedDropdownOpenedBy(prev => ({ ...prev, [name]: null }));
    } else {
      // Open by click
      setOpenNestedDropdown(name);
      setNestedDropdownOpenedBy(prev => ({ ...prev, [name]: 'click' }));
    }
  };

  // Mobile sidebar dropdown toggle
  const toggleSidebarDropdown = (name) => {
    setSidebarDropdown(sidebarDropdown === name ? null : name);
    setSidebarNestedDropdown(null);
  };

  // Mobile sidebar nested dropdown toggle
  const toggleSidebarNestedDropdown = (name) => {
    setSidebarNestedDropdown(sidebarNestedDropdown === name ? null : name);
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setOpenDropdown(null);
    setOpenNestedDropdown(null);
    setDropdownOpenedBy({});
    setNestedDropdownOpenedBy({});
    setSidebarDropdown(null);
    setSidebarNestedDropdown(null);
  };

  // ========== MEMBER LOGIN/LOGOUT HANDLERS ==========
  const handleMemberLoginClick = () => {
    if (isMemberLoggedIn) {
      // If already logged in, go to dashboard
      navigate("/members/members-dashboard");
    } else {
      // If not logged in, go to login page
      navigate("/members/members-area");
    }
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleMemberLogout = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('member_token');
      
      // Call logout endpoint
      if (token) {
        await axios.post('https://backend.dlapakistan.com/api/members/logout/', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(() => {
          // Ignore errors on logout
        });
      }
    } catch (error) {
      console.log('Logout API error (non-critical):', error);
      // Continue with client-side logout even if API call fails
    }

    // Clear all member storage
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_data');
    localStorage.removeItem('token_expiry');

    // Clear axios default header if it exists
    if (axios.defaults.headers.common['Authorization']) {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Update state
    setIsMemberLoggedIn(false);
    setMemberData(null);

    // Show success message
    Swal.fire({
      icon: 'success',
      title: 'Signed Out',
      text: 'You have been signed out successfully.',
      timer: 2000,
      showConfirmButton: false,
    });

    // Navigate to login page
    navigate("/members/members-area");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Get member name for display
  const getMemberDisplayName = () => {
    if (!memberData) return "";

    // Try to get lab name from member data
    if (memberData.first_name && memberData.last_name) {
      return `${memberData.first_name} ${memberData.last_name}`;
    } else if (memberData.email) {
      return memberData.email.split('@')[0];
    }
    return "Member";
  };

  // ========== BASIC NAVIGATION HANDLERS ==========
  const handleHomeClick = () => {
    navigate("/");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleContactClick = () => {
    navigate("/contact");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleMembersAreaClick = () => {
    if (isMemberLoggedIn) {
      navigate("/members/members-dashboard");
    } else {
      navigate("/members/members-area");
    }
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleBasketClick = () => {
    navigate("/basket");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== STORE BUTTON HANDLER ==========
  const handleStoreClick = () => {
    navigate("/store");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== DLA SHOP CLICK HANDLERS ==========
  const handleDlaShopMainClick = () => {
    navigate("/dlashop");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Arrow पर click करने के लिए - सिर्फ dropdown toggle करे (CLICK MODE)
  const handleDlaShopArrowClick = (e) => {
    e.stopPropagation();
    if (windowWidth > 1175.56) {
      toggleDropdownByClick("dlaShop");
    } else {
      // Mobile पर sidebar dropdown toggle करे
      toggleSidebarDropdown("dlaShop");
    }
  };

  const handleDlaShopCategoryClick = (category) => {
    navigate(`/dlashop?category=${encodeURIComponent(category)}`);
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== DLA SHOP CATEGORIES ==========
  const dlaShopCategories = [
    "All Products",
    "CPD Products",
    "Dentist Database",
    "GDPR Section",
    "Health and Safety",
    "Human Resources",
    "Lab Infection Control Pack",
    "Available to purchase Mouthguard Pack",
    "Lab Business Contract (Terms and Conditions of Trade)"
  ];

  // ========== DLA TRADE CLICK HANDLERS ==========
  const handleDlaTradeMainClick = () => {
    navigate("/dlatrade");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Arrow पर click करने के लिए - सिर्फ dropdown toggle करे (CLICK MODE)
  const handleDlaTradeArrowClick = (e) => {
    e.stopPropagation();
    if (windowWidth > 1175.56) {
      toggleDropdownByClick("dlaTrade");
    } else {
      // Mobile पर sidebar dropdown toggle करे
      toggleSidebarDropdown("dlaTrade");
    }
  };

  const handleDlaTradeCategoryClick = (category) => {
    navigate(`/dlatrade?category=${encodeURIComponent(category)}`);
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== DLA TRADE CATEGORIES ==========
  const dlaTradeCategories = [
    "DLA Headline Sponsor",
    "Sponsorship Opportunities",
    "DLA Trade Awards",
    "A – Z Directory of Affiliate Members",
    "Become an Affiliate Member"
  ];

  // ========== ABOUT DLA HANDLERS ==========
  // About DLA main page
  const handleAboutMainClick = () => {
    navigate("/about");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // About DLA arrow click handler (CLICK MODE)
  const handleAboutArrowClick = (e) => {
    e.stopPropagation();
    if (windowWidth > 1175.56) {
      toggleDropdownByClick("about");
    } else {
      // Mobile पर sidebar dropdown toggle करे
      toggleSidebarDropdown("about");
    }
  };

  // Meet the Board page
  const handleMeetTheBoard = () => {
    navigate("/about/meet-the-board");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Work We Do page
  const handleWorkWeDo = () => {
    navigate("/about/work-we-do");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Work We Do ke sub-items
  const handleEducation = () => {
    navigate("/about/work-we-do/education");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleRegulation = () => {
    navigate("/about/work-we-do/regulation");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleMembershipSupport = () => {
    navigate("/about/work-we-do/membership-support");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Our Policies page
  const handleOurPolicies = () => {
    navigate("/about/our-policies");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Our Policies ke sub-items - ये IMPORTANT है!
  const handleOperationalPolicies = () => {
    navigate("/about/our-policies/operational");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handlePoliticalPolicies = () => {
    navigate("/about/our-policies/political");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleGeneralPolicies = () => {
    navigate("/about/our-policies/general");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Become a Member page
  const handleBecomeMember = () => {
    navigate("/about/become-member");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Become a Member ke sub-items
  const handleReasonsToJoin = () => {
    navigate("/about/become-member/reasons");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleJoinToday = () => {
    navigate("/about/become-member/join");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleDLACodeOfConduct = () => {
    navigate("/about/become-member/code-of-conduct");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleMembershipTerms = () => {
    navigate("/about/become-member/terms");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleBenefitsMeetExperts = () => {
    navigate("/about/become-member/benefits-experts");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // DLA Fellowship page
  const handleDLAFellowship = () => {
    navigate("/about/dla-fellowship");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // DLA Fellowship ke sub-items
  const handleDLAFellowshipAwards = () => {
    navigate("/about/dla-fellowship-awards");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleDLASpecialRecognition = () => {
    navigate("/about/dla-special-recognition-award");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== DENTAL NEWS SECTION HANDLERS ==========
  const handleNewsMainClick = () => {
    navigate("/dental/news");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // Dental News arrow click handler (CLICK MODE)
  const handleNewsArrowClick = (e) => {
    e.stopPropagation();
    if (windowWidth > 1175.56) {
      toggleDropdownByClick("/news");
    }
  };

  const handleNewsLatest = () => {
    navigate("/dental/news");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  const handleNewsEvents = () => {
    navigate("/dental/journal");
    setSidebarOpen(false);
    closeAllDropdowns();
  };

  // ========== HOVER HANDLERS FOR DESKTOP ==========
  // Desktop par hover karne se dropdown khulega (only if not opened by click)
  const handleMouseEnter = (name) => {
    if (windowWidth > 1175.56 && dropdownOpenedBy[name] !== 'click') {
      setOpenDropdown(name);
      setDropdownOpenedBy(prev => ({ ...prev, [name]: 'hover' }));
      setOpenNestedDropdown(null);
      setNestedDropdownOpenedBy({});
    }
  };

  // Desktop par hover hatane se dropdown band hoga (only if opened by hover)
  const handleMouseLeave = (name) => {
    if (windowWidth > 1175.56 && dropdownOpenedBy[name] === 'hover') {
      setOpenDropdown(null);
      setDropdownOpenedBy(prev => ({ ...prev, [name]: null }));
      setOpenNestedDropdown(null);
      setNestedDropdownOpenedBy({});
    }
  };

  // Nested dropdown ke liye hover handler
  const handleNestedMouseEnter = (name) => {
    if (windowWidth > 1175.56 && nestedDropdownOpenedBy[name] !== 'click') {
      setOpenNestedDropdown(name);
      setNestedDropdownOpenedBy(prev => ({ ...prev, [name]: 'hover' }));
    }
  };

  // Nested dropdown ke liye hover leave handler
  const handleNestedMouseLeave = (name) => {
    if (windowWidth > 1175.56 && nestedDropdownOpenedBy[name] === 'hover') {
      setOpenNestedDropdown(null);
      setNestedDropdownOpenedBy(prev => ({ ...prev, [name]: null }));
    }
  };

  // Nested arrow par click karne ke liye (CLICK MODE)
  const handleNestedArrowClick = (e, name) => {
    e.stopPropagation();
    toggleNestedDropdownByClick(name);
  };

  const showMenuButton = windowWidth <= 1175.56;
  const showCard = windowWidth <= 1175.56;

  return (
    <header className="w-full h-32 fixed top-0 z-50 bg-white">
      {/* TOP HEADER SECTION */}
      <div className="flex justify-between items-center px-4 sm:px-8 py-3">
        <div className="flex items-center gap-4">
          <img
            src="/logo.png"
            alt="Hira Dental Lab"
            className="h-16 sm:h-24 cursor-pointer"
            onClick={handleHomeClick}
          />
        </div>

        {/* RIGHT SIDE - VERTICAL COLUMN LAYOUT */}
        {!showMenuButton && (
          <div className="hidden sm:flex flex-col items-end gap-1">
            {/* Contact Info - Email and Phone stacked vertically */}
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2 text-gray-700 text-sm sm:text-lg">
                <MdEmail className="text-[#7db63c] text-lg sm:text-xl" />
                <span>info@dlapakistan.com</span>
                <div className="flex items-center gap-2 text-gray-700 text-sm sm:text-lg">
                  <FaPhoneAlt className="text-[#7db63c] text-lg sm:text-xl" />
                  <span>+92 328 9045585</span>
                </div>
              </div>
            </div>

            {/* Member Login/Status Section - below contact info */}
            <div className="mt-2">
              {isMemberLoggedIn ? (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2 text-[#7db63c] text-sm">
                    <FaUser className="text-sm" />
                    <span className="font-medium">
                      Welcome, {getMemberDisplayName()}.
                    </span>
                    <button
                      onClick={handleMemberLogout}
                      className="px-3 py-1.5 text-xs text-[#7db63c] rounded-md transition-colors font-medium"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleMemberLoginClick}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-[#7db63c] hover:text-[#6aa437] rounded-md transition-colors font-medium"
                >
                  <FaLock className="text-xs" />
                  <span>Member Login</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* MENU BUTTON - mobile/tablet */}
        {showMenuButton && (
          <div className="flex items-center gap-4">
            {/* Mobile Member Login/Status Button */}
            <div className="flex items-center">
              {isMemberLoggedIn ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-[#7db63c] text-xs">
                    <FaUser className="text-xs" />
                    <span className="font-medium">{getMemberDisplayName()}</span>
                  </div>
                  <button
                    onClick={handleMemberLogout}
                    className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors font-medium"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleMemberLoginClick}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#7db63c] text-white hover:bg-[#6aa437] rounded-md transition-colors font-medium"
                >
                  <FaLock className="text-xs" />
                  <span>Login</span>
                </button>
              )}
            </div>

            <button
              data-menu-button
              className="text-2xl text-gray-700 p-2 hover:bg-gray-100 rounded"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        )}
      </div>



      {/* DESKTOP NAVBAR WITH DROPDOWNS */}
      {!showMenuButton && (
        <nav className="bg-[#7db63c] w-full relative">
          <ul
            ref={dropdownRef}
            className="flex flex-wrap justify-center items-center gap-6 px-4 sm:px-8 py-3 text-white text-sm sm:text-[16px] font-bold"
          >
            {/* Home Button */}
            <li
              className="px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors"
              onClick={handleHomeClick}
            >
              Home
            </li>

            {/* Store Button - NEW BUTTON ADDED */}
            <li
              className="px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors"
              onClick={handleStoreClick}
            >
              Store
            </li>

            {/* About DLA - WITH DROPDOWN */}
            <li
              className="relative"
              onMouseEnter={() => handleMouseEnter("about")}
              onMouseLeave={() => handleMouseLeave("about")}
            >
              <div className="flex items-center gap-1 px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors">
                <span onClick={handleAboutMainClick}>About DLA</span>
                <div className="flex items-center dropdown-arrow" onClick={handleAboutArrowClick}>
                  <FaChevronDown
                    className={`text-xs transition-transform duration-300 cursor-pointer ${
                      openDropdown === "about" ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>

              {/* About DLA dropdown items - GREEN CSS ADDED */}
              {openDropdown === "about" && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-50">
                  <div className="py-2">
                    <button
                      className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                      onClick={handleAboutMainClick}
                    >
                      About DLA Overview
                    </button>

                    <div className="border-t border-white/20">
                      <button
                        className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                        onClick={handleMeetTheBoard}
                      >
                        Meet the Board
                      </button>

                      {/* Work We Do - with nested dropdown - GREEN CSS ADDED */}
                      <div className="relative group"
                           onMouseEnter={() => handleNestedMouseEnter("workWeDo")}
                           onMouseLeave={() => handleNestedMouseLeave("workWeDo")}>
                        <button
                          className="flex justify-between items-center w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                          onClick={handleWorkWeDo}
                        >
                          Work We Do
                          <FaChevronRight className="text-xs" onClick={(e) => handleNestedArrowClick(e, "workWeDo")} />
                        </button>
                        {openNestedDropdown === "workWeDo" && (
                          <div className="absolute left-full top-0 ml-1 w-56 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-10">
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleEducation}
                            >
                              Education
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleRegulation}
                            >
                              Regulation
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleMembershipSupport}
                            >
                              Membership Support
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Our Policies - with nested dropdown - GREEN CSS ADDED */}
                      <div className="relative group"
                           onMouseEnter={() => handleNestedMouseEnter("ourPolicies")}
                           onMouseLeave={() => handleNestedMouseLeave("ourPolicies")}>
                        <button
                          className="flex justify-between items-center w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                          onClick={handleOurPolicies}
                        >
                          Our Policies
                          <FaChevronRight className="text-xs" onClick={(e) => handleNestedArrowClick(e, "ourPolicies")} />
                        </button>
                        {openNestedDropdown === "ourPolicies" && (
                          <div className="absolute left-full top-0 ml-1 w-56 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-10">
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleOperationalPolicies}
                            >
                              Operational Policies
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handlePoliticalPolicies}
                            >
                              Political Policies
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleGeneralPolicies}
                            >
                              General Policies
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Become a Member - with nested dropdown - GREEN CSS ADDED */}
                      <div className="relative group"
                           onMouseEnter={() => handleNestedMouseEnter("becomeMember")}
                           onMouseLeave={() => handleNestedMouseLeave("becomeMember")}>
                        <button
                          className="flex justify-between items-center w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                          onClick={handleBecomeMember}
                        >
                          Become a Member
                          <FaChevronRight className="text-xs" onClick={(e) => handleNestedArrowClick(e, "becomeMember")} />
                        </button>
                        {openNestedDropdown === "becomeMember" && (
                          <div className="absolute left-full top-0 ml-1 w-64 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-10">
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleReasonsToJoin}
                            >
                              Reasons to Join
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleJoinToday}
                            >
                              Join Today
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleDLACodeOfConduct}
                            >
                              DLA Code of Conduct
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleMembershipTerms}
                            >
                              Membership Terms
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleBenefitsMeetExperts}
                            >
                              Benefits & Meet Experts
                            </button>
                          </div>
                        )}
                      </div>

                      {/* DLA Fellowship - with nested dropdown - GREEN CSS ADDED */}
                      <div className="relative group"
                           onMouseEnter={() => handleNestedMouseEnter("dlaFellowship")}
                           onMouseLeave={() => handleNestedMouseLeave("dlaFellowship")}>
                        <button
                          className="flex justify-between items-center w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                          onClick={handleDLAFellowship}
                        >
                          DLA Fellowship
                          <FaChevronRight className="text-xs" onClick={(e) => handleNestedArrowClick(e, "dlaFellowship")} />
                        </button>
                        {openNestedDropdown === "dlaFellowship" && (
                          <div className="absolute left-full top-0 ml-1 w-56 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-10">
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleDLAFellowshipAwards}
                            >
                              DLA Fellowship Awards
                            </button>
                            <button
                              className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white text-sm"
                              onClick={handleDLASpecialRecognition}
                            >
                              Special Recognition Award
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>

            {/* Dental News - WITH DROPDOWN - GREEN CSS ADDED */}
            <li
              className="relative"
              onMouseEnter={() => handleMouseEnter("news")}
              onMouseLeave={() => handleMouseLeave("news")}
            >
              <div className="flex items-center gap-1 px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors">
                <span onClick={handleNewsMainClick}>Dental News</span>
                <div className="flex items-center dropdown-arrow" onClick={handleNewsArrowClick}>
                  <FaChevronDown
                    className={`text-xs transition-transform duration-300 cursor-pointer ${
                      openDropdown === "news" ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>
              {openDropdown === "news" && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-50">
                  <div className="py-2">
                    <button
                      className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                      onClick={handleNewsLatest}
                    >
                      Latest News
                    </button>
                    <button
                      className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors"
                      onClick={handleNewsEvents}
                    >
                      The DentalLab Journal
                    </button>
                  </div>
                </div>
              )}
            </li>

            {/* DLA Shop - WITH DROPDOWN - GREEN CSS ADDED */}
            <li
              className="relative dla-shop-main"
              onMouseEnter={() => handleMouseEnter("dlaShop")}
              onMouseLeave={() => handleMouseLeave("dlaShop")}
            >
              <div className="flex items-center gap-1 px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors">
                <span onClick={handleDlaShopMainClick}>DLA Shop</span>
                <div className="flex items-center dropdown-arrow" onClick={handleDlaShopArrowClick}>
                  <FaChevronDown
                    className={`text-xs transition-transform duration-300 cursor-pointer ${
                      openDropdown === "dlaShop" ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>
              {openDropdown === "dlaShop" && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-50">
                  <div className="py-2 max-h-96 overflow-y-auto">
                    <button
                      className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors font-medium border-b border-white/20"
                      onClick={handleDlaShopMainClick}
                    >
                      DLA Shop Overview
                    </button>
                    {dlaShopCategories.map((category, index) => (
                      <button
                        key={index}
                        className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors text-sm"
                        onClick={() => handleDlaShopCategoryClick(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>

            {/* DLA Trade - WITH DROPDOWN - GREEN CSS ADDED */}
            <li
              className="relative dla-trade-main"
              onMouseEnter={() => handleMouseEnter("dlaTrade")}
              onMouseLeave={() => handleMouseLeave("dlaTrade")}
            >
              <div className="flex items-center gap-1 px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors">
                <span onClick={handleDlaTradeMainClick}>DLA Trade</span>
                <div className="flex items-center dropdown-arrow" onClick={handleDlaTradeArrowClick}>
                  <FaChevronDown
                    className={`text-xs transition-transform duration-300 cursor-pointer ${
                      openDropdown === "dlaTrade" ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>
              {openDropdown === "dlaTrade" && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-[#7db63c] border border-white/20 shadow-xl rounded-md z-50">
                  <div className="py-2">
                    <button
                      className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors font-medium border-b border-white/20"
                      onClick={handleDlaTradeMainClick}
                    >
                      DLA Trade Overview
                    </button>
                    {dlaTradeCategories.map((category, index) => (
                      <button
                        key={index}
                        className="block w-full text-left px-4 py-3 hover:bg-[#6aa437] text-white transition-colors text-sm"
                        onClick={() => handleDlaTradeCategoryClick(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>

            {/* Contact Us */}
            <li
              className="px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors"
              onClick={handleContactClick}
            >
              Contact Us
            </li>

            {/* Members Area - WITH LOGIN STATUS ICON */}
            <li
              className="px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors flex items-center gap-2"
              onClick={handleMembersAreaClick}
            >
              <span>Members Area</span>
              {isMemberLoggedIn ? (
                <FaLockOpen className="text-sm text-white" title="Logged In" />
              ) : (
                <FaLock className="text-sm text-white" title="Login Required" />
              )}
            </li>

            {/* Basket */}
            <li
              className="px-3 py-2 cursor-pointer hover:underline hover:bg-white/10 rounded transition-colors"
              onClick={handleBasketClick}
            >
              Basket
            </li>
          </ul>
        </nav>
      )}

      {/* SIDEBAR - mobile/tablet */}
      {showMenuButton && (
        <>
          {/* Overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[998]"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar Panel */}
          <div
            ref={sidebarRef}
            className={`fixed top-0 left-0 h-full w-96 bg-[#7db63c] text-white z-[999] transform transition-transform duration-300 overflow-y-auto ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="sticky top-0 bg-[#7db63c] z-10 flex justify-between items-center p-4 border-b border-white/20">
              <span className="font-bold text-lg">Menu</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-white/20 rounded transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-4">
              {/* Member Status in Sidebar */}
              {isMemberLoggedIn && (
                <div className="mb-4 p-3 bg-[#6aa437] rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <FaUser className="text-sm" />
                    <span className="font-medium">Welcome, {getMemberDisplayName()}</span>
                  </div>
                  <button
                    onClick={handleMemberLogout}
                    className="w-full text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}

              <ul className="space-y-1">
                {/* Home */}
                <li>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors"
                    onClick={handleHomeClick}
                  >
                    Home
                  </button>
                </li>

                {/* Store - NEW BUTTON ADDED IN SIDEBAR */}
                <li>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors"
                    onClick={handleStoreClick}
                  >
                    Store
                  </button>
                </li>
                
                {/* About DLA - SIDEBAR VERSION (no changes needed here) */}
                <li className="border-b border-white/10 pb-2">
                  <div className="flex justify-between items-center px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors">
                    <button
                      className="text-left flex-1"
                      onClick={handleAboutMainClick}
                    >
                      About DLA
                    </button>
                    <button
                      className="p-1 hover:bg-white/20 rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSidebarDropdown("about");
                      }}
                    >
                      <FaChevronDown 
                        className={`transition-transform duration-200 ${
                          sidebarDropdown === "about" ? "rotate-180" : ""
                        }`} 
                      />
                    </button>
                  </div>
                  
                  {sidebarDropdown === "about" && (
                    <div className="ml-4 mt-1 space-y-1">
                      {/* Meet the Board */}
                      <button
                        className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                        onClick={handleMeetTheBoard}
                      >
                        Meet the Board
                      </button>
                      
                      {/* Work We Do */}
                      <div>
                        <div className="flex justify-between items-center px-4 py-2 hover:bg-[#6aa437] rounded transition-colors">
                          <button
                            className="text-left flex-1"
                            onClick={handleWorkWeDo}
                          >
                            Work We Do
                          </button>
                          <button
                            className="p-1 hover:bg-white/20 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSidebarNestedDropdown("workWeDo");
                            }}
                          >
                            <FaChevronRight 
                              className={`transition-transform duration-200 ${
                                sidebarNestedDropdown === "workWeDo" ? "rotate-90" : ""
                              }`} 
                            />
                          </button>
                        </div>
                        
                        {sidebarNestedDropdown === "workWeDo" && (
                          <div className="ml-4 mt-1 space-y-1 bg-[#5a902f]/50 rounded p-2">
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleEducation}
                            >
                              Education
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleRegulation}
                            >
                              Regulation
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleMembershipSupport}
                            >
                              Membership Support
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Our Policies */}
                      <div>
                        <div className="flex justify-between items-center px-4 py-2 hover:bg-[#6aa437] rounded transition-colors">
                          <button
                            className="text-left flex-1"
                            onClick={handleOurPolicies}
                          >
                            Our Policies
                          </button>
                          <button
                            className="p-1 hover:bg-white/20 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSidebarNestedDropdown("ourPolicies");
                            }}
                          >
                            <FaChevronRight 
                              className={`transition-transform duration-200 ${
                                sidebarNestedDropdown === "ourPolicies" ? "rotate-90" : ""
                              }`} 
                            />
                          </button>
                        </div>
                        
                        {sidebarNestedDropdown === "ourPolicies" && (
                          <div className="ml-4 mt-1 space-y-1 bg-[#5a902f]/50 rounded p-2">
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleOperationalPolicies}
                            >
                              Operational Policies
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handlePoliticalPolicies}
                            >
                              Political Policies
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleGeneralPolicies}
                            >
                              General Policies
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Become a Member */}
                      <div>
                        <div className="flex justify-between items-center px-4 py-2 hover:bg-[#6aa437] rounded transition-colors">
                          <button
                            className="text-left flex-1"
                            onClick={handleBecomeMember}
                          >
                            Become a Member
                          </button>
                          <button
                            className="p-1 hover:bg-white/20 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSidebarNestedDropdown("becomeMember");
                            }}
                          >
                            <FaChevronRight 
                              className={`transition-transform duration-200 ${
                                sidebarNestedDropdown === "becomeMember" ? "rotate-90" : ""
                              }`} 
                            />
                          </button>
                        </div>
                        
                        {sidebarNestedDropdown === "becomeMember" && (
                          <div className="ml-4 mt-1 space-y-1 bg-[#5a902f]/50 rounded p-2">
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleReasonsToJoin}
                            >
                              Reasons to Join
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleJoinToday}
                            >
                              Join Today
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleDLACodeOfConduct}
                            >
                              DLA Code of Conduct
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleMembershipTerms}
                            >
                              Membership Terms and Conditions
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleBenefitsMeetExperts}
                            >
                              Benefits: Meet the Experts
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* DLA Fellowship */}
                      <div>
                        <div className="flex justify-between items-center px-4 py-2 hover:bg-[#6aa437] rounded transition-colors">
                          <button
                            className="text-left flex-1"
                            onClick={handleDLAFellowship}
                          >
                            DLA Fellowship
                          </button>
                          <button
                            className="p-1 hover:bg-white/20 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSidebarNestedDropdown("dlaFellowship");
                            }}
                          >
                            <FaChevronRight 
                              className={`transition-transform duration-200 ${
                                sidebarNestedDropdown === "dlaFellowship" ? "rotate-90" : ""
                              }`} 
                            />
                          </button>
                        </div>
                        
                        {sidebarNestedDropdown === "dlaFellowship" && (
                          <div className="ml-4 mt-1 space-y-1 bg-[#5a902f]/50 rounded p-2">
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleDLAFellowshipAwards}
                            >
                              DLA Fellowship Awards
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                              onClick={handleDLASpecialRecognition}
                            >
                              DLA Special Recognition Award
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </li>
                
                {/* Dental News */}
                <li className="border-b border-white/10 pb-2">
                  <div className="flex justify-between items-center px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors">
                    <button
                      className="text-left flex-1"
                      onClick={handleNewsMainClick}
                    >
                      Dental News
                    </button>
                    <button
                      className="p-1 hover:bg-white/20 rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSidebarDropdown("news");
                      }}
                    >
                      <FaChevronDown 
                        className={`transition-transform duration-200 ${
                          sidebarDropdown === "news" ? "rotate-180" : ""
                        }`} 
                      />
                    </button>
                  </div>
                  
                  {sidebarDropdown === "news" && (
                    <div className="ml-4 mt-1 space-y-1">
                      <button
                        className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                        onClick={handleNewsLatest}
                      >
                        Latest News
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                        onClick={handleNewsEvents}
                      >
                        The DentalLab Journal
                      </button>
                    </div>
                  )}
                </li>
                
                {/* DLA Shop */}
                <li className="border-b border-white/10 pb-2">
                  <div className="flex justify-between items-center px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors">
                    <button
                      className="text-left flex-1"
                      onClick={handleDlaShopMainClick}
                    >
                      DLA Shop
                    </button>
                    <button
                      className="p-1 hover:bg-white/20 rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSidebarDropdown("dlaShop");
                      }}
                    >
                      <FaChevronDown 
                        className={`transition-transform duration-200 ${
                          sidebarDropdown === "dlaShop" ? "rotate-180" : ""
                        }`} 
                      />
                    </button>
                  </div>
                  
                  {sidebarDropdown === "dlaShop" && (
                    <div className="ml-4 mt-1 max-h-48 overflow-y-auto">
                      {dlaShopCategories.map((category, index) => (
                        <button
                          key={index}
                          className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                          onClick={() => handleDlaShopCategoryClick(category)}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
                
                {/* DLA Trade */}
                <li className="border-b border-white/10 pb-2">
                  <div className="flex justify-between items-center px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors">
                    <button
                      className="text-left flex-1"
                      onClick={handleDlaTradeMainClick}
                    >
                      DLA Trade
                    </button>
                    <button
                      className="p-1 hover:bg-white/20 rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSidebarDropdown("dlaTrade");
                      }}
                    >
                      <FaChevronDown 
                        className={`transition-transform duration-200 ${
                          sidebarDropdown === "dlaTrade" ? "rotate-180" : ""
                        }`} 
                      />
                    </button>
                  </div>
                  
                  {sidebarDropdown === "dlaTrade" && (
                    <div className="ml-4 mt-1 max-h-48 overflow-y-auto">
                      {dlaTradeCategories.map((category, index) => (
                        <button
                          key={index}
                          className="block w-full text-left px-4 py-2 hover:bg-[#6aa437] rounded transition-colors"
                          onClick={() => handleDlaTradeCategoryClick(category)}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
                
                {/* Contact Us */}
                <li>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors"
                    onClick={handleContactClick}
                  >
                    Contact Us
                  </button>
                </li>
                
                {/* Members Area - WITH LOGIN STATUS */}
                <li>
                  <div className="px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors flex justify-between items-center">
                    <button
                      className="text-left flex items-center gap-2"
                      onClick={handleMembersAreaClick}
                    >
                      <span>Members Area</span>
                      {isMemberLoggedIn ? (
                        <FaLockOpen className="text-sm" title="Logged In" />
                      ) : (
                        <FaLock className="text-sm" title="Login Required" />
                      )}
                    </button>
                    {!isMemberLoggedIn && (
                      <button
                        className="text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemberLoginClick();
                        }}
                      >
                        Login
                      </button>
                    )}
                  </div>
                </li>
                
                {/* Basket */}
                <li>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-[#6aa437] rounded-md transition-colors"
                    onClick={handleBasketClick}
                  >
                    Basket
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;